import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Row, Col, FormText } from 'reactstrap';
import { isNumber, Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/shared/reducers/user-management';
import { ITopic } from 'app/shared/model/topic.model';
import { getEntities as getTopics } from 'app/entities/topic/topic.reducer';
import { IAttachment } from 'app/shared/model/attachment.model';
import { getEntities as getAttachments } from 'app/entities/attachment/attachment.reducer';
import { IRecordedMeeting } from 'app/shared/model/recorded-meeting.model';
import { getEntities as getRecordedMeetings } from 'app/entities/recorded-meeting/recorded-meeting.reducer';
import { IMeeting } from 'app/shared/model/meeting.model';
import { MeetingStatus } from 'app/shared/model/enumerations/meeting-status.model';
import { MeetingType } from 'app/shared/model/enumerations/meeting-type.model';
import { MeetingScope } from 'app/shared/model/enumerations/meeting-scope.model';
import { getEntity, updateEntity, createEntity, reset } from './meeting.reducer';

export const MeetingUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const topics = useAppSelector(state => state.topic.entities);
  const attachments = useAppSelector(state => state.attachment.entities);
  const recordedMeetings = useAppSelector(state => state.recordedMeeting.entities);
  const meetingEntity = useAppSelector(state => state.meeting.entity);
  const loading = useAppSelector(state => state.meeting.loading);
  const updating = useAppSelector(state => state.meeting.updating);
  const updateSuccess = useAppSelector(state => state.meeting.updateSuccess);
  const meetingStatusValues = Object.keys(MeetingStatus);
  const meetingTypeValues = Object.keys(MeetingType);
  const meetingScopeValues = Object.keys(MeetingScope);

  const handleClose = () => {
    navigate('/meeting' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getTopics({}));
    dispatch(getAttachments({}));
    dispatch(getRecordedMeetings({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    const entity = {
      ...meetingEntity,
      ...values,
      topics: mapIdList(values.topics),
      attachments: mapIdList(values.attachments),
      user: users.find(it => it.id.toString() === values.user.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          statusType: 'Processed',
          type: 'Interview',
          meetingScope: 'Internal',
          ...meetingEntity,
          user: meetingEntity?.user?.id,
          topics: meetingEntity?.topics?.map(e => e.id.toString()),
          attachments: meetingEntity?.attachments?.map(e => e.id.toString()),
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="fivePlusApp.meeting.home.createOrEditLabel" data-cy="MeetingCreateUpdateHeading">
            <Translate contentKey="fivePlusApp.meeting.home.createOrEditLabel">Create or edit a Meeting</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="meeting-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField label={translate('fivePlusApp.meeting.name')} id="meeting-name" name="name" data-cy="name" type="text" />
              <ValidatedField
                label={translate('fivePlusApp.meeting.meetingTime')}
                id="meeting-meetingTime"
                name="meetingTime"
                data-cy="meetingTime"
                type="text"
              />
              <ValidatedField
                label={translate('fivePlusApp.meeting.duration')}
                id="meeting-duration"
                name="duration"
                data-cy="duration"
                type="text"
              />
              <ValidatedField
                label={translate('fivePlusApp.meeting.statusType')}
                id="meeting-statusType"
                name="statusType"
                data-cy="statusType"
                type="select"
              >
                {meetingStatusValues.map(meetingStatus => (
                  <option value={meetingStatus} key={meetingStatus}>
                    {translate('fivePlusApp.MeetingStatus.' + meetingStatus)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('fivePlusApp.meeting.dailyCoLink')}
                id="meeting-dailyCoLink"
                name="dailyCoLink"
                data-cy="dailyCoLink"
                type="text"
              />
              <ValidatedField
                label={translate('fivePlusApp.meeting.daliyCoRoom')}
                id="meeting-daliyCoRoom"
                name="daliyCoRoom"
                data-cy="daliyCoRoom"
                type="text"
              />
              <ValidatedField label={translate('fivePlusApp.meeting.type')} id="meeting-type" name="type" data-cy="type" type="select">
                {meetingTypeValues.map(meetingType => (
                  <option value={meetingType} key={meetingType}>
                    {translate('fivePlusApp.MeetingType.' + meetingType)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('fivePlusApp.meeting.approved')}
                id="meeting-approved"
                name="approved"
                data-cy="approved"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('fivePlusApp.meeting.metaData')}
                id="meeting-metaData"
                name="metaData"
                data-cy="metaData"
                type="textarea"
              />
              <ValidatedField
                label={translate('fivePlusApp.meeting.meetingScope')}
                id="meeting-meetingScope"
                name="meetingScope"
                data-cy="meetingScope"
                type="select"
              >
                {meetingScopeValues.map(meetingScope => (
                  <option value={meetingScope} key={meetingScope}>
                    {translate('fivePlusApp.MeetingScope.' + meetingScope)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField id="meeting-user" name="user" data-cy="user" label={translate('fivePlusApp.meeting.user')} type="select">
                <option value="" key="0" />
                {users
                  ? users.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('fivePlusApp.meeting.topic')}
                id="meeting-topic"
                data-cy="topic"
                type="select"
                multiple
                name="topics"
              >
                <option value="" key="0" />
                {topics
                  ? topics.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('fivePlusApp.meeting.attachment')}
                id="meeting-attachment"
                data-cy="attachment"
                type="select"
                multiple
                name="attachments"
              >
                <option value="" key="0" />
                {attachments
                  ? attachments.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/meeting" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default MeetingUpdate;
